// frontend/src/components/Modals/ClientMatchSelector.jsx

import React, { useState, useEffect, useRef } from 'react';

const ClientMatchSelector = ({ similarClients, extractedData, selectedClient, onSelect }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientData, setNewClientData] = useState({
    name: '',
    account_manager: '',
    contact: '',
    email: '',
    phone: '',
    rfc: '',
    address: '',
  });

  // ✅ NUEVO: Ref para controlar si ya se inicializó
  const initializedRef = useRef(false);

  // ✅ CORREGIDO: Solo inicializar UNA VEZ cuando se monta el componente
  useEffect(() => {
    if (extractedData && !initializedRef.current) {
      initializedRef.current = true;
      
      const accountManager = selectedClient?.type === 'existing' && selectedClient?.clientData
        ? selectedClient.clientData.account_manager
        : extractedData.account_manager || '';
    
      setNewClientData({
        name: extractedData.client_name || '',
        account_manager: accountManager,
        contact: extractedData.contact_name || '',
        email: extractedData.contact_email || '',
        phone: extractedData.contact_phone || '', 
        rfc: '',
        address: '',
      });
    }
  }, []); // ✅ SIN DEPENDENCIAS - Solo se ejecuta al montar

  // ✅ NUEVO: Resetear cuando se desmonta
  useEffect(() => {
    return () => {
      initializedRef.current = false;
    };
  }, []);

  const handleSelectClient = (clientId) => {
    setSelectedOption(clientId);
    setShowNewClientForm(false);
    onSelect({ type: 'existing', clientId });
  };

  const handleSelectNew = () => {
    setSelectedOption('new');
    setShowNewClientForm(true);
    onSelect({ type: 'new', data: newClientData });
  };

  const handleNewClientChange = (field, value) => {
    const updatedData = { ...newClientData, [field]: value };
    setNewClientData(updatedData);
    if (selectedOption === 'new') {
      onSelect({ type: 'new', data: updatedData });
    }
  };

  return (
    <div>
      <h6 className="text-uppercase text-xs font-weight-bold mb-3">
        <i className="ni ni-circle-08 me-2"></i>
        Selecciona un cliente
      </h6>

      {/* Lista de clientes similares */}
      {similarClients && similarClients.length > 0 && (
        <div className="mb-3">
          <label className="form-control-label text-uppercase text-xs font-weight-bold text-muted mb-2">
            Clientes similares encontrados:
          </label>
          {similarClients.map((match) => (
            <div
              key={match.client.id}
              className={`card shadow-sm mb-2 cursor-pointer ${
                selectedOption === match.client.id ? 'border-primary border-2' : 'border-0'
              }`}
              onClick={() => handleSelectClient(match.client.id)}
              style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
            >
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="custom-control custom-radio">
                    <input
                      type="radio"
                      id={`client-${match.client.id}`}
                      name="clientSelection"
                      className="custom-control-input"
                      checked={selectedOption === match.client.id}
                      onChange={() => handleSelectClient(match.client.id)}
                    />
                    <label
                      className="custom-control-label"
                      htmlFor={`client-${match.client.id}`}
                    >
                      <strong className="text-dark">{match.client.name}</strong>
                      <br />
                      <small className="text-muted">
                        ID: {match.client.client_id}
                        {match.client.email && ` • ${match.client.email}`}
                      </small>
                    </label>
                  </div>
                  <div>
                    <span className="badge badge-success">
                      {match.score}% similar
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Opción: Crear nuevo cliente */}
      <div
        className={`card shadow-sm mb-3 cursor-pointer ${
          selectedOption === 'new' ? 'border-primary border-2' : 'border-0'
        }`}
        onClick={handleSelectNew}
        style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
      >
        <div className="card-body p-3">
          <div className="custom-control custom-radio">
            <input
              type="radio"
              id="client-new"
              name="clientSelection"
              className="custom-control-input"
              checked={selectedOption === 'new'}
              onChange={handleSelectNew}
            />
            <label className="custom-control-label" htmlFor="client-new">
              <strong className="text-dark">
                <i className="ni ni-fat-add me-2"></i>
                Crear nuevo cliente
              </strong>
            </label>
          </div>
        </div>
      </div>

      {/* Formulario de nuevo cliente - PRE-LLENADO */}
      {showNewClientForm && (
        <div className="card shadow border-0 bg-secondary">
          <div className="card-body p-3">
            <h6 className="text-uppercase text-xs font-weight-bold mb-3">
              <i className="ni ni-building me-2"></i>
              Datos del nuevo cliente
            </h6>
            
            <div className=" mb-3" role="alert">
              <span className="alert-icon"><i className="ni ni-bulb-61"></i></span>
              <span className="alert-text">
                Los datos han sido pre-llenados desde el PDF. Revisa y modifica si es necesario.
              </span>
            </div>

            <div className="form-group mb-3">
              <label className="form-control-label text-uppercase text-xs font-weight-bold">
                Nombre del cliente <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control form-control-alternative"
                value={newClientData.name}
                onChange={(e) => handleNewClientChange('name', e.target.value)}
                placeholder="Nombre completo del cliente"
              />
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="form-control-label text-uppercase text-xs font-weight-bold">
                    Contacto
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-alternative"
                    value={newClientData.contact}
                    onChange={(e) => handleNewClientChange('contact', e.target.value)}
                    placeholder="Nombre del contacto"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="form-control-label text-uppercase text-xs font-weight-bold">
                    Email
                  </label>
                  <input
                    type="email"
                    className="form-control form-control-alternative"
                    value={newClientData.email}
                    onChange={(e) => handleNewClientChange('email', e.target.value)}
                    placeholder="email@cliente.com"
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="form-control-label text-uppercase text-xs font-weight-bold">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    className="form-control form-control-alternative"
                    value={newClientData.phone}
                    onChange={(e) => handleNewClientChange('phone', e.target.value)}
                    placeholder="0000000"
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="form-control-label text-uppercase text-xs font-weight-bold">
                    RFC
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-alternative"
                    value={newClientData.rfc}
                    onChange={(e) => handleNewClientChange('rfc', e.target.value)}
                    placeholder="ABC........."
                  />
                </div>
              </div>
            </div>

            <div className="form-group mb-0">
              <label className="form-control-label text-uppercase text-xs font-weight-bold">
                Dirección
              </label>
              <textarea
                className="form-control form-control-alternative"
                rows="2"
                value={newClientData.address}
                onChange={(e) => handleNewClientChange('address', e.target.value)}
                placeholder="Dirección completa"
              ></textarea>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientMatchSelector;
